import React, { RefObject, useEffect, useRef, useState } from "react";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import "./QuillEditor.css";

const useQuill = () => {
  const quillRef: RefObject<any> = useRef();
  const toolbarRef: RefObject<any> = useRef();

  const [isLoaded, setIsLoaded] = useState(false);
  const [quill, setQuill] = useState<Quill | undefined>(undefined);

  useEffect(() => {
    if (
      !quill &&
      quillRef &&
      quillRef.current &&
      toolbarRef &&
      toolbarRef.current &&
      isLoaded
    ) {
      const quill = new Quill(quillRef.current, {
        modules: { toolbar: toolbarRef.current },
        theme: "snow",
      });

      setQuill(quill);
    }

    // Quill needs to be initialized for the first time on render, but not after that.
    setIsLoaded(true);
  }, [isLoaded, quill, quillRef, toolbarRef]);

  return {
    quill,
    quillRef,
    toolbarRef,
  };
};

type QuillEditorProps = {
  content: string;
  onContentChanged: (newContent: string) => void;
};

function QuillEditor({ content, onContentChanged }: QuillEditorProps) {
  const { quill, quillRef, toolbarRef } = useQuill();
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    if (isLoaded || !quill || quillRef.current === null) {
      return;
    }

    quill.clipboard.dangerouslyPasteHTML(content);

    setIsLoaded(true);
  }, [content, isLoaded, quill, quillRef]);

  useEffect(() => {
    if (!isLoaded || !quill || quillRef.current === null) {
      return () => {};
    }

    const handleContentChanged = () => onContentChanged(quill.root.innerHTML);

    quill.on("text-change", handleContentChanged);

    return () => quill.off("text-change", handleContentChanged);
  }, [content, isLoaded, onContentChanged, quill, quillRef]);

  return (
    <>
      <div ref={toolbarRef}>
        <button className="ql-bold">Bold</button>
        <button className="ql-italic">Italic</button>
        <button className="ql-underline">Underline</button>
      </div>
      <div ref={quillRef}></div>
    </>
  );
}

export default QuillEditor;
